import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import { useEffect, useState } from 'react';

import type { ReasonModel } from '@/domain/models/reason.js';

export interface DocumentRejectDialogProps {
    open: boolean;
    reasons: ReasonModel[];
    submitting: boolean;
    onClose: () => void;
    onConfirm: (reasonId: string, notes: string) => void;
    translate: (key: string) => string;
}

const MIN_JUSTIFICATION_LENGTH = 10;

export function DocumentRejectDialog({ open, reasons, submitting, onClose, onConfirm, translate }: DocumentRejectDialogProps) {
    const [reasonId, setReasonId] = useState('');
    const [notes, setNotes] = useState('');

    useEffect(() => {
        if (open) {
            setReasonId('');
            setNotes('');
        }
    }, [open]);

    const trimmedNotes = notes.trim();
    const notesLength = trimmedNotes.length;
    const isNotesTooShort = notesLength > 0 && notesLength < MIN_JUSTIFICATION_LENGTH;
    const canConfirm = reasonId !== '' && notesLength >= MIN_JUSTIFICATION_LENGTH && !submitting;

    const handleConfirm = () => {
        if (!canConfirm) {
            return;
        }
        onConfirm(reasonId, trimmedNotes);
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>{translate('documents.rejectDoc')}</DialogTitle>
            <DialogContent>
                <TextField
                    select
                    label={translate('documents.reason')}
                    value={reasonId}
                    onChange={(e) => setReasonId(e.target.value)}
                    fullWidth
                    size="small"
                    sx={{ mt: 1, mb: 2 }}
                >
                    {reasons.map((r) => (
                        <MenuItem key={r.id} value={r.id}>
                            {r.label}
                        </MenuItem>
                    ))}
                </TextField>
                <TextField
                    label={translate('documents.justification')}
                    placeholder={translate('documents.justificationPlaceholder')}
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    fullWidth
                    multiline
                    minRows={3}
                    size="small"
                    helperText={isNotesTooShort ? translate('documents.justificationMin10') : undefined}
                    error={isNotesTooShort}
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={submitting}>
                    {translate('common.cancel')}
                </Button>
                <Button variant="contained" color="error" sx={{ color: 'white' }} onClick={handleConfirm} disabled={!canConfirm}>
                    {submitting ? translate('documents.rejecting') : translate('documents.rejectDoc')}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
